import { useState, useEffect, useCallback, useRef } from 'react';
import { api, ChatMessageResponse, ChatAttachmentRef } from '@/lib/api';

const MESSAGE_PAGE_SIZE = 50;

interface UseChatMessagesReturn {
  messages: ChatMessageResponse[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  sendMessage: (
    content: string,
    replyToId?: string,
    attachmentRefs?: ChatAttachmentRef[],
  ) => Promise<ChatMessageResponse | null>;
  editMessage: (messageId: string, content: string) => Promise<boolean>;
  deleteMessage: (messageId: string) => Promise<boolean>;
  loadMore: () => void;
  addMessage: (message: ChatMessageResponse) => void;
  updateMessage: (messageId: string, updates: Partial<ChatMessageResponse>) => void;
  removeMessage: (messageId: string) => void;
  markAsRead: (messageId: string) => Promise<void>;
  refresh: () => Promise<void>;
}

export function useChatMessages(channelId: string | null): UseChatMessagesReturn {
  const [messages, setMessages] = useState<ChatMessageResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const oldestMessageIdRef = useRef<string | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!channelId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await api.listChatMessages(channelId, { limit: MESSAGE_PAGE_SIZE });
      // API returns newest-first; reverse for chronological (oldest-first) display
      setMessages([...result.messages].reverse());
      setHasMore(result.hasMore);
      oldestMessageIdRef.current = result.oldestMessageId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  const loadMore = useCallback(async () => {
    if (!channelId || loadingMore || !hasMore || !oldestMessageIdRef.current) return;
    setLoadingMore(true);
    try {
      const result = await api.listChatMessages(channelId, {
        before: oldestMessageIdRef.current,
        limit: 50,
      });
      // API returns newest-first; reverse for chronological (oldest-first) display
      const olderMessages = [...result.messages].reverse();
      setMessages((prev) => [...olderMessages, ...prev]);
      setHasMore(result.hasMore);
      oldestMessageIdRef.current = result.oldestMessageId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more messages');
    } finally {
      setLoadingMore(false);
    }
  }, [channelId, loadingMore, hasMore]);

  const sendMessage = useCallback(
    async (
      content: string,
      replyToId?: string,
      attachmentRefs?: ChatAttachmentRef[],
    ): Promise<ChatMessageResponse | null> => {
      if (!channelId) return null;
      try {
        const result = await api.sendChatMessage(channelId, { content, replyToId, attachmentRefs });
        // Add to local message list immediately
        if (result.message) {
          setMessages((prev) => {
            if (prev.some((m) => m.messageId === result.message.messageId)) return prev;
            return [...prev, result.message];
          });
        }
        return result.message;
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to send message');
        return null;
      }
    },
    [channelId],
  );

  const editMessage = useCallback(async (messageId: string, content: string): Promise<boolean> => {
    try {
      const result = await api.editChatMessage(messageId, content);
      setMessages((prev) =>
        prev.map((m) => (m.messageId === messageId ? { ...m, ...result.message } : m)),
      );
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to edit message');
      return false;
    }
  }, []);

  const deleteMessage = useCallback(async (messageId: string): Promise<boolean> => {
    try {
      await api.deleteChatMessage(messageId);
      setMessages((prev) => prev.filter((m) => m.messageId !== messageId));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete message');
      return false;
    }
  }, []);

  const addMessage = useCallback((message: ChatMessageResponse) => {
    setMessages((prev) => {
      // Avoid duplicates
      if (prev.some((m) => m.messageId === message.messageId)) return prev;
      return [...prev, message];
    });
  }, []);

  const updateMessage = useCallback((messageId: string, updates: Partial<ChatMessageResponse>) => {
    setMessages((prev) => prev.map((m) => (m.messageId === messageId ? { ...m, ...updates } : m)));
  }, []);

  const removeMessage = useCallback((messageId: string) => {
    setMessages((prev) => prev.filter((m) => m.messageId !== messageId));
  }, []);

  // Mark the newest message in the channel as read
  const markAsRead = useCallback(
    async (messageId: string) => {
      if (!channelId) return;
      try {
        await api.markChatChannelRead(channelId, messageId);
      } catch {
        // Non-critical — silently ignore
      }
    },
    [channelId],
  );

  useEffect(() => {
    if (channelId) {
      fetchMessages();
    } else {
      setMessages([]);
      setHasMore(false);
      oldestMessageIdRef.current = null;
    }
  }, [channelId, fetchMessages]);

  return {
    messages,
    loading,
    loadingMore,
    error,
    hasMore,
    sendMessage,
    editMessage,
    deleteMessage,
    loadMore,
    addMessage,
    updateMessage,
    removeMessage,
    markAsRead,
    refresh: fetchMessages,
  };
}
